/*
 * Copyright (c) 2020. American Express Company
 */

import React, { Component, ReactComponentElement } from 'react';
import _ from 'lodash';

// models and services
import { CardModel } from 'src/types/card.model';
import CardService from '../../services/card.service';
import FinanceService, { CanAffordPurchase } from '../../services/finance.service';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import BwSlider from '../bw-slider/BwSlider';
import EditableField from 'src/bw/editable-field/EditableField';
import { FieldControl, Label } from '@americanexpress/dls-react';
import GoalDrawer from './GoalDrawer';
import Output from '../atomic/molecules/output/Output';
import SavingsGoal from 'src/bw/bw-drawer/common/SavingsGoal';

// icons and styles
import { ReactComponent as LargePurchase } from 'src/bw/goal/large-purchase.svg';

export default class LargePurchaseDrawer extends Component<any> {
    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;
        const card = drawerProps.card;
        const purchasePrice = Math.abs(card.attributes.totalAmount || 0);

        const canAfford: CanAffordPurchase = FinanceService.canAffordPurchase(
            card.startDate,
            purchasePrice,
            card.monthlySavings,
            card.monthlyFreeCash
        );

        const purchaseNameDiv: JSX.Element =
            card.nickname && card.created !== card.modified ? null : (
                <FieldControl>
                    <Label htmlFor="purchase-name">What do you want to purchase?</Label>
                    <EditableField
                        id="purchase-name"
                        className="no-float"
                        value={card.attributes.purchaseName ? card.attributes.purchaseName : ''}
                        editValue={card.attributes.purchaseName ? card.attributes.purchaseName : ''}
                        handleSave={(value: string) =>
                            this.props.handleChangeCardAttribute(card, value, 'purchaseName', 'string')
                        }
                        editing={true}
                        fullWidth
                        maxLength={55}
                    />
                </FieldControl>
            );

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleUpdateCard={this.handleUpdateCard.bind(this)}
                handleChangeCard={this.props.handleChangeCard}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<LargePurchase />}
                introContent={LiteralsService.getLiteral('LargePurchaseDrawer.intro')}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.props.handleChangeCardDate.bind(this)}
                startDateLabel={LiteralsService.getLiteral('LargePurchaseDrawer.startDate')}
            >
                {purchaseNameDiv}

                <Output
                    title={card.nickname ? `${card.nickname} cost` : 'Purchase cost'}
                    main={FormatService.format(purchasePrice)}
                    canAfford={canAfford}
                />

                <BwSlider
                    id="large-purchase-slider"
                    label="How much is your purchase"
                    value={purchasePrice}
                    formattedValue={FormatService.formatCompactMoney(purchasePrice)}
                    step={1000}
                    min={0}
                    max={400000}
                    onChange={(value: any) => this.handleSaveCost(value)}
                    onDone={(value: any) => this.handleUpdateSavingsGoal(value)}
                    key="vacation-cost-slider"
                    inputToggleText="Add a precise cost"
                />

                <SavingsGoal
                    drawerProps={drawerProps}
                    user={this.props.user}
                    setDrawer={this.props.setDrawer}
                    handleOpenSignup={() => {
                        console.warn('Deprecated: handleOpenSignup (Take Vacation)');
                        this.props.handleOpenSignup();
                    }}
                    handleCreateLinkedSavingsCard={this.props.handleCreateLinkedSavingsCard}
                    handleChangeCardAttribute={this.props.handleChangeCardAttribute}
                    handleUpdateCard={this.props.handleUpdateCard}
                    handleDeleteCard={this.props.handleDeleteCard}
                />
            </GoalDrawer>
        );
    }

    private handleSaveCost(value: number): void {
        this.props.handleChangeCardAttribute(this.props.drawerProps.card, -Number(value || 0), 'totalAmount');
    }

    private handleUpdateSavingsGoal(value: number): void {
        if (CardService.hasSavingsGoal(this.props)) {
            const updatedCard: CardModel = CardService.getSavingsGoal(this.props);
            _.set(updatedCard, 'attributes.targetBalance', Number(value || 0));
            this.props.handleUpdateCard(updatedCard);
        }
    }

    private handleUpdateCard(): void {
        if (this.props.drawerProps.card.attributes.purchaseName) {
            this.props.drawerProps.card.nickname = this.props.drawerProps.card.nickname
                ? this.props.drawerProps.card.nickname
                : `Save for ${this.props.drawerProps.card.attributes.purchaseName}`;
        }
        this.props.handleUpdateCard();
    }
}
